import { Link, useParams } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import Seo from "@/components/feature/Seo";
import ArticleCard from "@/components/feature/ArticleCard";
import { articles, type ArticleMeta } from "@/mocks/articles";
import { categories } from "@/mocks/site";

function PickHero({ article, slug }: { article: ArticleMeta; slug: string }) {
  return (
    <Link
      to={`/article/${article.slug}`}
      className="group grid overflow-hidden rounded-2xl border border-background-200/80 bg-background-50 transition-all duration-300 hover:border-primary-300 md:grid-cols-2"
    >
      <div className="relative aspect-[16/10] overflow-hidden md:aspect-auto">
        <img
          src={article.cover}
          alt={article.coverAlt}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute left-4 top-4 rounded-md bg-primary-500 px-2.5 py-1 text-xs font-semibold text-background-50">
          本栏目编辑力荐
        </span>
      </div>
      <div className="flex flex-col justify-center p-6 md:p-10">
        <div className="flex items-center gap-2 text-xs text-foreground-500">
          <span className="text-primary-600">{article.date}</span>
          <span>·</span>
          <span>{article.readTime} 读完</span>
        </div>
        <h3 className="mt-3 font-heading text-xl font-bold leading-snug text-foreground-950 transition-colors group-hover:text-primary-700 md:text-2xl">
          {article.title}
        </h3>
        <p className="mt-3 text-sm leading-7 text-foreground-600 md:line-clamp-3">{article.excerpt}</p>
        <div className="mt-5 flex items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent-500 text-sm font-bold text-background-50">
            {article.author.charAt(0)}
          </span>
          <span className="text-sm font-medium text-foreground-800">{article.author} · {article.authorRole}</span>
          <span className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-primary-600">
            立即阅读 <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
          </span>
        </div>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug = "" } = useParams();
  const category = categories.find((c) => c.slug === slug);
  const list = category ? articles.filter((a) => a.category === category.slug) : [];

  if (!category) {
    return (
      <PageLayout dark={false}>
        <Seo title="栏目不存在 - 看料" />
        <div className="flex min-h-[70vh] flex-col items-center justify-center px-4 pt-20 text-center">
          <i className="ri-emotion-sad-line text-6xl text-foreground-300" />
          <h1 className="mt-4 font-heading text-2xl font-bold text-foreground-950">这个栏目不存在</h1>
          <p className="mt-2 text-sm text-foreground-600">可能已经被调整或合并了。</p>
          <Link
            to="/"
            className="mt-6 inline-flex h-11 items-center gap-2 rounded-md bg-primary-500 px-6 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
          >
            <i className="ri-home-line" /> 返回首页
          </Link>
        </div>
      </PageLayout>
    );
  }

  const pick = list[0];
  const rest = list.slice(1);

  return (
    <PageLayout dark={false}>
      <Seo
        title={`${category.name} - ${category.desc}`}
        description={`看料${category.name}栏目，${category.desc}精选与推荐值得读的好料，编辑团队每日更新。`}
        keywords={`看料,${category.name},看料精选,${category.name}推荐,看料内容`}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: `看料 · ${category.name}`,
            description: category.desc,
            url: `https://01x0wyo.cn/category/${category.slug}`,
            inLanguage: "zh-CN",
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: "https://01x0wyo.cn/" },
              { "@type": "ListItem", position: 2, name: category.name, item: `https://01x0wyo.cn/category/${category.slug}` },
            ],
          },
        ]}
      />

      <section className="bg-background-50 pt-24 md:pt-28">
        <div className="mx-auto max-w-7xl px-4 pb-6 md:px-6">
          <nav aria-label="面包屑" className="flex items-center gap-1.5 text-xs text-foreground-500">
            <Link to="/" className="transition-colors hover:text-primary-700">首页</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-foreground-800">{category.name}</span>
          </nav>
          <div className="mt-8 rounded-2xl border border-background-200/80 bg-background-100 p-6 md:p-10">
            <span className="flex h-14 w-14 items-center justify-center rounded-xl bg-secondary-500 text-3xl text-background-50">
              <i className={category.icon} />
            </span>
            <h1 className="mt-5 font-heading text-3xl font-black text-foreground-950 md:text-5xl">
              {category.name}
            </h1>
            <p className="mt-3 max-w-2xl text-base leading-8 text-foreground-700">{category.desc}</p>
            <div className="mt-4 flex flex-wrap items-center gap-2 text-xs text-foreground-500">
              <span className="rounded-full bg-background-50 px-3 py-1 font-medium">共 {list.length} 篇好料</span>
              <span className="rounded-full bg-background-50 px-3 py-1 font-medium">编辑每日更新</span>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="mx-auto max-w-7xl px-4 py-10 md:px-6 md:py-14">
          <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
            「{category.name}」精选好料
          </h2>
          <div className="mt-8 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {pick && (
              <div className="md:col-span-2 lg:col-span-3">
                <PickHero article={pick} slug={slug} />
              </div>
            )}
            {rest.map((a) => (
              <ArticleCard key={a.slug} article={a} />
            ))}
          </div>
        </div>
      </section>
    </PageLayout>
  );
}